// features/procurement/RequisitionListPage.tsx
/**
 * Purchase Requisitions Management — Executive UI/UX Redesign
 * Featuring quick status filters, instant approvals, smart convert-to-PO modal,
 * and zero-silent-fail error handling.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import {
    ClipboardList, Plus, Search, X, RefreshCw,
    CheckCircle, Clock, Ban, FileText, ArrowLeft,
    AlertCircle, ChevronRight, Truck, Store,
    Zap, Calendar, Building2, Eye, ArrowUpRight,
    Filter
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getRequisitions,
    approveRequisition,
    rejectRequisition,
    convertRequisitionToPO,
    getVendors,
    autoGenerateRequisitions,
    getProcurementErrorMessage,
    PurchaseRequisition,
    Vendor,
} from './services/procurementApi';

// ============================================================
// STATUS PILL COMPONENT
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    draft: { label: 'Draft', bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
    pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    approved: { label: 'Approved', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    rejected: { label: 'Rejected', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
    converted_to_po: { label: 'Converted to PO', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

function StatusPill({ status }: { status: string }) {
    const config = STATUS_CONFIG[status] || {
        label: status.replace(/_/g, ' '),
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        dot: 'bg-slate-400',
    };

    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', config.bg, config.text)}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', config.dot)} />
            {config.label}
        </span>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function RequisitionListPage() {
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const tenantId = user?.tenantId || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Modals
    const [rejectTarget, setRejectTarget] = useState<PurchaseRequisition | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [convertTarget, setConvertTarget] = useState<PurchaseRequisition | null>(null);
    const [selectedVendorId, setSelectedVendorId] = useState('');

    // ============================================================
    // QUERIES
    // ============================================================

    const { data: requisitions, isLoading, refetch } = useQuery<PurchaseRequisition[]>({
        queryKey: ['requisitions-list', tenantId],
        queryFn: async () => {
            const result = await getRequisitions(tenantId, { limit: 100 });
            return result.requisitions;
        },
        enabled: !!tenantId,
    });

    const { data: vendors } = useQuery<Vendor[]>({
        queryKey: ['vendors-dropdown', tenantId],
        queryFn: () => getVendors(tenantId),
        enabled: !!tenantId && !!convertTarget,
    });

    // ============================================================
    // MUTATIONS
    // ============================================================

    const approveMutation = useMutation({
        mutationFn: (reqId: string) => approveRequisition(tenantId, reqId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-requisitions'] });
            addToast({
                type: 'success',
                title: 'Requisition Approved',
                message: 'Items are now authorized for purchase order conversion.',
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Approval Failed',
                message: getProcurementErrorMessage(err, 'Unable to approve requisition.'),
            });
        },
    });

    const rejectMutation = useMutation({
        mutationFn: () => rejectRequisition(tenantId, rejectTarget!.id, rejectReason.trim()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-requisitions'] });
            setRejectTarget(null);
            setRejectReason('');
            addToast({
                type: 'success',
                title: 'Requisition Rejected',
                message: 'The requisition was flagged and returned with notes.',
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Rejection Failed',
                message: getProcurementErrorMessage(err, 'Unable to reject requisition.'),
            });
        },
    });

    const convertMutation = useMutation({
        mutationFn: () => convertRequisitionToPO(tenantId, convertTarget!.id, selectedVendorId),
        onSuccess: (po: any) => {
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-requisitions'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-orders'] });
            setConvertTarget(null);
            setSelectedVendorId('');
            addToast({
                type: 'success',
                title: 'PO Created Successfully',
                message: `Converted to Purchase Order #${po.poNumber || po.id.slice(0, 8)}`,
            });
            navigate(`/procurement/orders/${po.id}`);
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Conversion Failed',
                message: getProcurementErrorMessage(err, 'Could not convert requisition to PO.'),
            });
        },
    });

    const autoRequisitionMutation = useMutation({
        mutationFn: () => autoGenerateRequisitions(tenantId, selectedBranchId || undefined),
        onSuccess: (result: any) => {
            addToast({
                type: 'success',
                title: 'Auto-Requisition Generated',
                message: `Created requisitions for ${result.productsRequested || 0} low-stock products.`,
            });
            queryClient.invalidateQueries({ queryKey: ['requisitions-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-requisitions'] });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Auto-Requisition Failed',
                message: getProcurementErrorMessage(err, 'Failed to auto-generate requisitions.'),
            });
        },
    });

    // ============================================================
    // FILTERED LIST & COUNTS
    // ============================================================

    const reqsList = requisitions || [];

    const filteredReqs = useMemo(() => {
        return reqsList.filter((r) => {
            const matchesSearch =
                !search ||
                r.requisitionNumber?.toLowerCase().includes(search.toLowerCase()) ||
                r.branchName?.toLowerCase().includes(search.toLowerCase()) ||
                r.notes?.toLowerCase().includes(search.toLowerCase());

            const matchesStatus =
                statusFilter === 'all' || r.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [reqsList, search, statusFilter]);

    const pendingCount = reqsList.filter((r) => r.status === 'pending_approval').length;
    const approvedCount = reqsList.filter((r) => r.status === 'approved').length;
    const convertedCount = reqsList.filter((r) => r.status === 'converted_to_po').length;

    const formatDate = (d: string) =>
        d ? new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1680px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/procurement')}
                                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
                                type="button"
                                title="Back to Procurement"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/20 text-white">
                                <ClipboardList className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-slate-900 tracking-tight">Purchase Requisitions</h1>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {reqsList.length} internal item requests • {pendingCount} awaiting approval
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5 flex-wrap">
                            <button
                                onClick={() => autoRequisitionMutation.mutate()}
                                disabled={autoRequisitionMutation.isPending}
                                className="px-3.5 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                                type="button"
                                title="Scan inventory and generate requisitions for low stock items"
                            >
                                {autoRequisitionMutation.isPending ? <Spinner size="xs" /> : <Zap size={14} className="text-amber-600" />}
                                <span>Auto-Requisition Low Stock</span>
                            </button>

                            <button
                                onClick={() => navigate('/procurement/requisitions/new')}
                                className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition font-bold text-xs flex items-center gap-2 shadow-md shadow-blue-500/20"
                                type="button"
                            >
                                <Plus size={16} />
                                <span>Create Requisition</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1680px] mx-auto px-6 lg:px-8 pt-6 space-y-6">

                {/* Metric Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Requisitions</span>
                        <p className="text-2xl font-black text-slate-900 mt-1">{reqsList.length}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Pending Approval</span>
                        <p className={clsx('text-2xl font-black mt-1', pendingCount > 0 ? 'text-amber-600' : 'text-slate-900')}>
                            {pendingCount}
                        </p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Approved for PO</span>
                        <p className="text-2xl font-black text-blue-600 mt-1">{approvedCount}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Converted to PO</span>
                        <p className="text-2xl font-black text-emerald-600 mt-1">{convertedCount}</p>
                    </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="relative w-full md:w-80">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by requisition #, branch, notes..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
                        />
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
                        {[
                            { id: 'all', label: 'All Requests' },
                            { id: 'pending_approval', label: 'Pending Approval' },
                            { id: 'approved', label: 'Approved' },
                            { id: 'converted_to_po', label: 'Converted to PO' },
                            { id: 'rejected', label: 'Rejected' },
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setStatusFilter(filter.id)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all',
                                    statusFilter === filter.id
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                                type="button"
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table View */}
                {isLoading ? (
                    <div className="py-20 text-center flex flex-col items-center justify-center">
                        <Spinner size="lg" />
                        <p className="text-sm font-medium text-slate-500 mt-4">Loading purchase requisitions...</p>
                    </div>
                ) : filteredReqs.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-4">
                            <ClipboardList size={28} />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">No Requisitions Found</h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                            {search ? `No requisition matching "${search}".` : 'Create internal item requisitions before generating purchase orders.'}
                        </p>
                        <button
                            onClick={() => navigate('/procurement/requisitions/new')}
                            className="mt-5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            type="button"
                        >
                            + Create New Requisition
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/70">
                                        <th className="py-3.5 px-6">Requisition #</th>
                                        <th className="py-3.5 px-6">Branch</th>
                                        <th className="py-3.5 px-6">Created</th>
                                        <th className="py-3.5 px-6">Status</th>
                                        <th className="py-3.5 px-6">Notes</th>
                                        <th className="py-3.5 px-6 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredReqs.map((req) => (
                                        <tr
                                            key={req.id}
                                            onClick={() => navigate(`/procurement/requisitions/${req.id}`)}
                                            className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                                        >
                                            <td className="py-4 px-6 font-mono font-bold text-slate-900 flex items-center gap-2">
                                                <span>{req.requisitionNumber}</span>
                                            </td>

                                            <td className="py-4 px-6 text-xs text-slate-700">
                                                <div className="flex items-center gap-2">
                                                    <Building2 size={13} className="text-slate-400" />
                                                    <span className="font-semibold">{req.branchName || 'Main Branch'}</span>
                                                </div>
                                            </td>

                                            <td className="py-4 px-6 text-xs text-slate-500">
                                                {formatDate(req.createdAt)}
                                            </td>

                                            <td className="py-4 px-6">
                                                <StatusPill status={req.status || 'draft'} />
                                            </td>

                                            <td className="py-4 px-6 text-xs text-slate-500 max-w-xs truncate">
                                                {req.notes || '—'}
                                            </td>

                                            <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {req.status === 'pending_approval' && (
                                                        <>
                                                            <button
                                                                onClick={() => approveMutation.mutate(req.id)}
                                                                disabled={approveMutation.isPending}
                                                                className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg transition"
                                                                type="button"
                                                                title="Approve Requisition"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setRejectTarget(req);
                                                                    setRejectReason('');
                                                                }}
                                                                className="px-3 py-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 rounded-lg transition"
                                                                type="button"
                                                                title="Reject Requisition"
                                                            >
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}

                                                    {req.status === 'approved' && (
                                                        <button
                                                            onClick={() => {
                                                                setConvertTarget(req);
                                                                setSelectedVendorId('');
                                                            }}
                                                            className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                            title="Convert into Purchase Order"
                                                        >
                                                            <FileText size={12} />
                                                            <span>Convert to PO</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => navigate(`/procurement/requisitions/${req.id}`)}
                                                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                                        type="button"
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================ */}
            {/* REJECT MODAL WITH REASON VALIDATION                          */}
            {/* ============================================================ */}
            <AnimatePresence>
                {rejectTarget && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setRejectTarget(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 text-left"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 rounded-xl bg-rose-100 text-rose-600">
                                    <Ban size={22} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-900">
                                        Reject Requisition #{rejectTarget.requisitionNumber}
                                    </h3>
                                    <p className="text-xs text-slate-500">Provide an explanation for the requesting branch</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                    Reason for Rejection <span className="text-rose-500">*</span>
                                </label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={3}
                                    placeholder="e.g., Insufficient budget allocation for this quarter..."
                                    className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-400 resize-none transition"
                                    autoFocus
                                />
                            </div>

                            <div className="flex items-center gap-2.5 mt-6 pt-3 border-t border-slate-100">
                                <button
                                    onClick={() => setRejectTarget(null)}
                                    className="flex-1 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => rejectMutation.mutate()}
                                    disabled={!rejectReason.trim() || rejectMutation.isPending}
                                    className="flex-1 py-2.5 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl text-xs font-bold hover:from-rose-600 transition shadow-md shadow-rose-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                    type="button"
                                >
                                    {rejectMutation.isPending ? <Spinner size="xs" variant="white" /> : <Ban size={14} />}
                                    <span>Reject Request</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ============================================================ */}
            {/* CONVERT TO PO MODAL                                         */}
            {/* ============================================================ */}
            <AnimatePresence>
                {convertTarget && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setConvertTarget(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 text-left"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2.5 rounded-xl bg-blue-100 text-blue-600">
                                    <FileText size={22} />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-slate-900">
                                        Convert #{convertTarget.requisitionNumber} to PO
                                    </h3>
                                    <p className="text-xs text-slate-500">Select the supplier who will fulfill this purchase</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">
                                    Target Supplier <span className="text-blue-500">*</span>
                                </label>
                                <select
                                    value={selectedVendorId}
                                    onChange={(e) => setSelectedVendorId(e.target.value)}
                                    className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
                                >
                                    <option value="">Select a registered supplier...</option>
                                    {(vendors || []).map((v) => (
                                        <option key={v.id} value={v.id}>
                                            {v.name} ({v.category || 'General'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex items-center gap-2.5 mt-6 pt-3 border-t border-slate-100">
                                <button
                                    onClick={() => setConvertTarget(null)}
                                    className="flex-1 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => convertMutation.mutate()}
                                    disabled={!selectedVendorId || convertMutation.isPending}
                                    className="flex-1 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-xs font-bold hover:from-blue-700 transition shadow-md shadow-blue-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                    type="button"
                                >
                                    {convertMutation.isPending ? <Spinner size="xs" variant="white" /> : <FileText size={14} />}
                                    <span>Create Purchase Order</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}